import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { Handshake, TrendingUp, Heart, Briefcase, FileText, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PartnerWithUs = () => {
  const [partnerSubmitting, setPartnerSubmitting] = useState(false);
  const [investorSubmitting, setInvestorSubmitting] = useState(false);
  const [partnerForm, setPartnerForm] = useState({
    name: '',
    email: '',
    organization: '',
    sector: '',
    type: '',
    proposal: ''
  });

  const [investorForm, setInvestorForm] = useState({
    name: '',
    email: '',
    investorType: '',
    interest: '',
    amount: ''
  });

  const handlePartnerSubmit = async (e) => {
    e.preventDefault();
    if (!partnerForm.sector || !partnerForm.type) {
      toast.error('Please select a sector and partnership type.');
      return;
    }
    setPartnerSubmitting(true);
    try {
      const res = await axios.post(`${API}/partnerships`, partnerForm);
      toast.success(res.data.message);
      setPartnerForm({
        name: '',
        email: '',
        organization: '',
        sector: '',
        type: '',
        proposal: ''
      });
    } catch (err) {
      toast.error('Submission failed. Please try again.');
    } finally {
      setPartnerSubmitting(false);
    }
  };

  const handleInvestorSubmit = async (e) => {
    e.preventDefault();
    if (!investorForm.investorType || !investorForm.interest || !investorForm.amount) {
      toast.error('Please complete all investor fields.');
      return;
    }
    setInvestorSubmitting(true);
    try {
      const res = await axios.post(`${API}/investors`, investorForm);
      toast.success(res.data.message);
      setInvestorForm({
        name: '',
        email: '',
        investorType: '',
        interest: '',
        amount: ''
      });
    } catch (err) {
      toast.error('Submission failed. Please try again.');
    } finally {
      setInvestorSubmitting(false);
    }
  };

  const opportunities = [
    {
      title: 'Solar Fund',
      capacity: '500MW',
      irr: '18%',
      description: 'Large-scale renewable energy infrastructure',
      stage: 'Active',
      color: '#00CED1',
      slug: 'solar-fund',
      image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?q=85&w=800'
    },
    {
      title: 'Smart Cities REIT',
      capacity: 'Multi-project',
      irr: '15%',
      description: 'Climate-resilient urban development',
      stage: 'Pipeline',
      color: '#FF8C00',
      slug: 'smart-cities-reit',
      image: 'https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?q=85&w=800'
    },
    {
      title: 'AgriTech VC Fund',
      capacity: '$10M',
      irr: '22%',
      description: 'Climate-smart agriculture startups',
      stage: 'Active',
      color: '#00CED1',
      slug: 'agritech-vc-fund',
      image: 'https://images.unsplash.com/photo-1625246333195-78d9c38ad449?q=85&w=800'
    }
  ];

  const careerPositions = [
    { role: 'Senior Energy Engineer', department: 'Energy', location: 'Harare', type: 'Full-time' },
    { role: 'Blockchain Developer', department: 'Technology', location: 'Remote', type: 'Full-time' },
    { role: 'Commodity Trading Specialist', department: 'Commodities', location: 'Johannesburg', type: 'Full-time' },
    { role: 'ESG Analyst', department: 'Sustainability', location: 'Harare', type: 'Full-time' },
    { role: 'Financial Analyst', department: 'Capital', location: 'Harare', type: 'Full-time' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1521791136064-7986c2920216?crop=entropy&cs=srgb&fm=jpg&q=85')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-[#00CED1]/60 to-transparent"></div>
          <div className="absolute inset-0 opacity-30" style={{
            backgroundImage: 'radial-gradient(circle at 80% 20%, rgba(255, 140, 0, 0.3) 0%, transparent 50%)',
          }}></div>
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-6 text-white text-center">
          <h1 className="text-6xl font-bold mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Partner With Us
          </h1>
          <p className="text-2xl text-gray-200">Join Africa's Sustainable Transformation</p>
        </div>
      </section>

      {/* Strategic Alliances */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Handshake className="w-16 h-16 text-[#00CED1] mx-auto mb-4" />
            <h2 className="text-5xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Strategic Alliances
            </h2>
            <div className="w-24 h-1 bg-[#00CED1] mx-auto mb-8"></div>
            <p className="text-xl text-gray-600">Building transformative partnerships across sectors</p>
          </div>

          <Card className="border-2 border-[#00CED1] mb-12">
            <CardContent className="p-8">
              <form onSubmit={handlePartnerSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="partner-name">Full Name *</Label>
                    <Input
                      id="partner-name"
                      value={partnerForm.name}
                      onChange={(e) => setPartnerForm({...partnerForm, name: e.target.value})}
                      required
                      placeholder="Your name"
                    />
                  </div>

                  <div>
                    <Label htmlFor="partner-email">Email *</Label>
                    <Input
                      id="partner-email"
                      type="email"
                      value={partnerForm.email}
                      onChange={(e) => setPartnerForm({...partnerForm, email: e.target.value})}
                      required
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="organization">Organization *</Label>
                    <Input
                      id="organization"
                      value={partnerForm.organization}
                      onChange={(e) => setPartnerForm({...partnerForm, organization: e.target.value})}
                      required
                      placeholder="Your organization"
                    />
                  </div>

                  <div>
                    <Label htmlFor="sector">Sector *</Label>
                    <Select 
                      value={partnerForm.sector} 
                      onValueChange={(value) => setPartnerForm({...partnerForm, sector: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select sector" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Energy">Energy</SelectItem>
                        <SelectItem value="Mining">Mining</SelectItem>
                        <SelectItem value="Agriculture">Agriculture</SelectItem>
                        <SelectItem value="Technology">Technology</SelectItem>
                        <SelectItem value="Finance">Finance</SelectItem>
                        <SelectItem value="Healthcare">Healthcare</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="type">Partnership Type *</Label>
                  <Select 
                    value={partnerForm.type} 
                    onValueChange={(value) => setPartnerForm({...partnerForm, type: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select partnership type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="strategic">Strategic Alliance</SelectItem>
                      <SelectItem value="distribution">Distribution Partner</SelectItem>
                      <SelectItem value="technology">Technology Partner</SelectItem>
                      <SelectItem value="supplier">Supplier/Vendor</SelectItem>
                      <SelectItem value="joint-venture">Joint Venture</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="proposal">Partnership Proposal *</Label>
                  <Textarea
                    id="proposal"
                    value={partnerForm.proposal}
                    onChange={(e) => setPartnerForm({...partnerForm, proposal: e.target.value})}
                    required
                    rows={6}
                    placeholder="Describe your partnership proposal and how we can collaborate..."
                  />
                </div>

                <Button 
                  type="submit" 
                  size="lg" 
                  disabled={partnerSubmitting}
                  data-testid="partnership-submit-button"
                  className="w-full bg-[#FF8C00] hover:bg-[#e07d00] text-white font-bold transition-all duration-300 transform hover:scale-105"
                >
                  {partnerSubmitting ? 'Submitting...' : 'Submit Partnership Proposal'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Investment Portal */}
      <section className="py-20 px-6 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <TrendingUp className="w-16 h-16 text-[#00CED1] mx-auto mb-4" />
            <h2 className="text-5xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Investment Opportunities
            </h2>
            <div className="w-24 h-1 bg-[#FF8C00] mx-auto mb-8"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {opportunities.map((opp, index) => (
              <Card 
                key={index} 
                className="hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden"
              >
                <div className="relative h-44 overflow-hidden">
                  <img src={opp.image} alt={opp.title} className="w-full h-full object-cover transition-transform duration-300 hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                </div>
                <div className="h-2" style={{ backgroundColor: opp.color }}></div>
                <CardHeader>
                  <div className="flex items-center justify-between mb-4">
                    <Link to={`/explore/${opp.slug}`} className="text-2xl font-bold text-gray-900 hover:text-[#00CED1] transition-colors" style={{ fontFamily: 'Montserrat, sans-serif' }} data-testid={`opportunity-title-link-${opp.slug}`}>
                      {opp.title}
                    </Link>
                    <Badge className={opp.stage === 'Active' ? 'bg-green-500' : 'bg-yellow-500'}>
                      {opp.stage}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 mb-6">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Capacity:</span>
                      <span className="font-bold text-gray-900">{opp.capacity}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Projected IRR:</span>
                      <Link to={`/explore/${opp.slug}`} className="font-bold text-[#00CED1] text-xl hover:underline underline-offset-4" data-testid={`opportunity-irr-link-${opp.slug}`}>{opp.irr}</Link>
                    </div>
                    <p className="text-gray-600">{opp.description}</p>
                  </div>
                  <Button 
                    className="w-full bg-[#FF8C00] hover:bg-[#e07d00] text-white font-semibold"
                    onClick={() => toast.info('Prospectus download initiated')}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Prospectus
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="border-2 border-[#00CED1]">
            <CardHeader className="bg-[#00CED1] text-white">
              <h3 className="text-2xl font-bold">Investor Registration</h3>
            </CardHeader>
            <CardContent className="p-8">
              <form onSubmit={handleInvestorSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="investor-name">Full Name *</Label>
                    <Input
                      id="investor-name"
                      value={investorForm.name}
                      onChange={(e) => setInvestorForm({...investorForm, name: e.target.value})}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="investor-email">Email *</Label>
                    <Input
                      id="investor-email"
                      type="email"
                      value={investorForm.email}
                      onChange={(e) => setInvestorForm({...investorForm, email: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <Label htmlFor="investor-type">Investor Type *</Label>
                    <Select 
                      value={investorForm.investorType} 
                      onValueChange={(value) => setInvestorForm({...investorForm, investorType: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="individual">Individual</SelectItem>
                        <SelectItem value="institutional">Institutional</SelectItem>
                        <SelectItem value="dfi">DFI</SelectItem>
                        <SelectItem value="family-office">Family Office</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="interest">Area of Interest *</Label>
                    <Select 
                      value={investorForm.interest} 
                      onValueChange={(value) => setInvestorForm({...investorForm, interest: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select area" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="energy">Renewable Energy</SelectItem>
                        <SelectItem value="real-estate">Real Estate</SelectItem>
                        <SelectItem value="vc">Venture Capital</SelectItem>
                        <SelectItem value="agriculture">Agriculture</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="amount">Investment Range *</Label>
                    <Select 
                      value={investorForm.amount} 
                      onValueChange={(value) => setInvestorForm({...investorForm, amount: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="100k-500k">$100K - $500K</SelectItem>
                        <SelectItem value="500k-1m">$500K - $1M</SelectItem>
                        <SelectItem value="1m-5m">$1M - $5M</SelectItem>
                        <SelectItem value="5m+">$5M+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  size="lg" 
                  disabled={investorSubmitting}
                  data-testid="investor-submit-button"
                  className="w-full bg-[#FF8C00] hover:bg-[#e07d00] text-white font-bold"
                >
                  {investorSubmitting ? 'Submitting...' : 'Register Interest'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Impact Philanthropy */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Heart className="w-16 h-16 text-[#FF8C00] mx-auto mb-4" />
            <h2 className="text-5xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Impact Philanthropy
            </h2>
            <div className="w-24 h-1 bg-[#00CED1] mx-auto mb-8"></div>
            <p className="text-xl text-gray-600">Transform lives through strategic giving</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <Card className="border-2 border-[#00CED1] hover:shadow-xl transition-all duration-300 overflow-hidden">
              <div className="relative h-44 overflow-hidden">
                <img src="https://images.unsplash.com/photo-1560493676-04071c5f467b?q=85&w=800" alt="Veteran solar irrigation farming" className="w-full h-full object-cover transition-transform duration-300 hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <Link to="/explore/impact-philanthropy" className="text-5xl font-bold text-[#00CED1] mb-2 block hover:underline underline-offset-8" data-testid="philanthropy-link-500">$500</Link>
                  <p className="text-gray-700">Solar irrigation for 1 veteran plot</p>
                </div>
                <Button className="w-full bg-[#00CED1] hover:bg-[#00b8bb] text-white font-semibold">
                  Donate Now
                </Button>
              </CardContent>
            </Card>

            <Card className="border-2 border-[#FF8C00] hover:shadow-xl transition-all duration-300 overflow-hidden">
              <div className="relative h-44 overflow-hidden">
                <img src="https://images.unsplash.com/photo-1622253692010-333f2da6031d?q=85&w=800" alt="Telemedicine clinic" className="w-full h-full object-cover transition-transform duration-300 hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <Link to="/explore/impact-philanthropy" className="text-5xl font-bold text-[#FF8C00] mb-2 block hover:underline underline-offset-8" data-testid="philanthropy-link-5000">$5,000</Link>
                  <p className="text-gray-700">Equip a telemedicine clinic</p>
                </div>
                <Button className="w-full bg-[#FF8C00] hover:bg-[#e07d00] text-white font-semibold">
                  Donate Now
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Career Gateway */}
      <section className="py-20 px-6 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Briefcase className="w-16 h-16 text-[#00CED1] mx-auto mb-4" />
            <h2 className="text-5xl font-bold mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Join Our Team
            </h2>
            <div className="w-24 h-1 bg-[#00CED1] mx-auto mb-8"></div>
            <p className="text-xl text-gray-300">Be part of Africa's transformation</p>
          </div>

          <div className="space-y-4">
            {careerPositions.map((position, index) => (
              <Card 
                key={index} 
                className="bg-white/10 border-gray-700 hover:bg-white/15 transition-all duration-300"
              >
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-white mb-2">{position.role}</h3>
                      <div className="flex flex-wrap gap-2">
                        <Badge className="bg-[#00CED1] hover:bg-[#00b8bb]">{position.department}</Badge>
                        <Badge variant="outline" className="border-gray-500 text-gray-300">{position.location}</Badge>
                        <Badge variant="outline" className="border-gray-500 text-gray-300">{position.type}</Badge>
                      </div>
                    </div>
                    <Button 
                      className="bg-[#FF8C00] hover:bg-[#e07d00] text-white font-semibold"
                      onClick={() => toast.info('Application form will open')}
                    >
                      Apply Now
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PartnerWithUs;